/**
 * inventorySavedViews — predefined + user-saved filter view bundles.
 *
 * Storage: localStorage, per-case, key `dc-inventory-views:<caseId>`.
 * Value shape:
 *   { views: [ { id, name, filters: [{axis,value},...] }, ... ],
 *     lastViewName: <string> | null }
 *
 * Active-view detection is by content equality (chip set match), not
 * by name — so a deep link with the right chips highlights the matching
 * tab automatically. Editing a saved view means delete-and-resave for
 * v1 (acceptable; cheap to upgrade later).
 */

export const PREDEFINED_VIEWS = [
  { id: 'sys-all', name: 'All', filters: [], system: true },
  {
    id: 'sys-needs-review', name: 'Needs Review', system: true,
    filters: [{ axis: 'labelStatus', value: 'corrected' }],
  },
  {
    id: 'sys-failed', name: 'Failed', system: true,
    filters: [{ axis: 'status', value: 'failed' }],
  },
  {
    id: 'sys-unclassified', name: 'Unclassified', system: true,
    filters: [{ axis: 'labelStatus', value: 'unclassified' }],
  },
];

const STORAGE_PREFIX = 'dc-inventory-views:';

function storageKey(caseId) {
  return `${STORAGE_PREFIX}${caseId}`;
}

export function loadUserViews(caseId) {
  if (!caseId || typeof localStorage === 'undefined') return { views: [], lastViewName: null };
  try {
    const raw = localStorage.getItem(storageKey(caseId));
    if (!raw) return { views: [], lastViewName: null };
    const parsed = JSON.parse(raw);
    return {
      views: Array.isArray(parsed.views) ? parsed.views : [],
      lastViewName: parsed.lastViewName || null,
    };
  } catch {
    return { views: [], lastViewName: null };
  }
}

function saveUserViews(caseId, state) {
  if (!caseId || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(storageKey(caseId), JSON.stringify(state));
  } catch {
    // non-critical
  }
}

export function addUserView(caseId, name, filters, columns, unit = null) {
  const trimmed = (name || '').trim();
  if (!trimmed) return null;
  const state = loadUserViews(caseId);
  // Prevent collisions with predefined names + existing user names.
  const exists = PREDEFINED_VIEWS.some(v => v.name.toLowerCase() === trimmed.toLowerCase())
    || state.views.some(v => v.name.toLowerCase() === trimmed.toLowerCase());
  if (exists) return null;
  const view = {
    id: `usr-${Date.now()}`,
    name: trimmed,
    filters: filters.map(f => ({ axis: f.axis, value: f.value })),
    // columns is optional — when omitted, the saved view inherits whatever
    // column set is active when the view is loaded (current behavior).
    ...(Array.isArray(columns) ? { columns: [...columns] } : {}),
    // unit is optional too — a view saved from the Documents unit reopens there.
    ...(unit === 'files' || unit === 'documents' ? { unit } : {}),
  };
  saveUserViews(caseId, { ...state, views: [...state.views, view] });
  return view;
}

export function deleteUserView(caseId, viewId) {
  const state = loadUserViews(caseId);
  saveUserViews(caseId, {
    ...state,
    views: state.views.filter(v => v.id !== viewId),
  });
}

export function setLastViewName(caseId, name) {
  const state = loadUserViews(caseId);
  saveUserViews(caseId, { ...state, lastViewName: name || null });
}

/** Order-insensitive set equality on {axis,value} chips. */
export function filtersEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const sig = (f) => `${f.axis}|${f.value}`;
  const setA = new Set(a.map(sig));
  for (const f of b) {
    if (!setA.has(sig(f))) return false;
  }
  return true;
}

/** All views (predefined + user) for a case. */
export function allViews(caseId) {
  const { views } = loadUserViews(caseId);
  return [...PREDEFINED_VIEWS, ...views];
}

/** Find the view whose chip set matches the given filters[], or null. */
export function findMatchingView(caseId, filters) {
  return allViews(caseId).find(v => filtersEqual(v.filters, filters)) || null;
}

/** Apply a view's filters by counting docs through applyAxisFilters. */
export function countViewMatches(view, docs, applyAxisFilters, ctx) {
  if (!docs) return 0;
  if (view.filters.length === 0) return docs.length;
  return applyAxisFilters(docs, view.filters, ctx).length;
}
